import { useState, useEffect } from 'react'
import { Loader2, BarChart3, RefreshCw, ShieldAlert } from 'lucide-react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { PageHeader, FluentCard } from '@/components/layout/page-header'
import { PermissionGate } from '@/components/auth/permission-gate'
import { analyticsService } from '@/lib/api/services/analytics'
import {
  AnalyticsDateRangePicker,
  OverviewCards,
  UsageTrendChart,
  TopTemplatesTable,
  TopUsersTable,
  UsageHeatmap,
  DepartmentBreakdown,
  EventsTable,
  ExportButton,
  TemplateAnalyticsCard,
} from '@/components/analytics'
import type {
  TenantAnalyticsOverview,
  TopTemplateAnalytics,
  TopUserAnalytics,
  UsageTrendData,
  HeatmapData,
  DepartmentUsage,
  TemplateAnalytics,
  TemplateUsageEvent,
  AggregationPeriod,
  AnalyticsFilters,
} from '@/lib/api/types'

export default function Analytics() {
  // Date range state
  const [startDate, setStartDate] = useState<string>(() => {
    const date = new Date()
    date.setDate(date.getDate() - 30)
    return date.toISOString().split('T')[0] ?? ''
  })
  const [endDate, setEndDate] = useState<string>(() => new Date().toISOString().split('T')[0] ?? '')
  const [period, setPeriod] = useState<AggregationPeriod>('Daily')

  // Data state
  const [overview, setOverview] = useState<TenantAnalyticsOverview | null>(null)
  const [topTemplates, setTopTemplates] = useState<TopTemplateAnalytics[]>([])
  const [topUsers, setTopUsers] = useState<TopUserAnalytics[]>([])
  const [usageTrends, setUsageTrends] = useState<UsageTrendData[]>([])
  const [heatmapData, setHeatmapData] = useState<HeatmapData[]>([])
  const [departmentUsage, setDepartmentUsage] = useState<DepartmentUsage[]>([])
  const [templateAnalytics, setTemplateAnalytics] = useState<TemplateAnalytics[]>([])
  const [recentEvents, setRecentEvents] = useState<TemplateUsageEvent[]>([])

  // Loading states
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const filters: AnalyticsFilters = {
    startDate,
    endDate,
    period,
  }

  const loadAnalytics = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true)
      } else {
        setLoading(true)
      }
      setError(null)

      // Load all data in parallel
      const [
        overviewData,
        topTemplatesData,
        topUsersData,
        trendsData,
        heatmapDataResult,
        departmentData,
        templatesData,
      ] = await Promise.all([
        analyticsService.getTemplateAnalyticsOverview(filters).catch(() => null),
        analyticsService.getTopTemplates(filters, 10).catch(() => []),
        analyticsService.getTopUsers(filters, 10).catch(() => []),
        analyticsService.getUsageTrends(filters).catch(() => []),
        analyticsService.getHeatmapData(filters).catch(() => []),
        analyticsService.getDepartmentUsage(filters).catch(() => []),
        analyticsService.getTemplateAnalytics(filters).catch(() => []),
      ])

      setOverview(overviewData)
      setTopTemplates(topTemplatesData)
      setTopUsers(topUsersData)
      setUsageTrends(trendsData)
      setHeatmapData(heatmapDataResult)
      setDepartmentUsage(departmentData)
      setTemplateAnalytics(templatesData)

      // Get recent events from first template or empty
      if (templatesData.length > 0 && templatesData[0]) {
        try {
          const events = await analyticsService.getTemplateEvents(templatesData[0].templateId, {
            ...filters,
            templateId: undefined, // Get all events
          })
          setRecentEvents(events.slice(0, 20))
        } catch {
          setRecentEvents([])
        }
      }
    } catch (err) {
      setError('Failed to load analytics data')
      console.error('Failed to load analytics:', err)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => {
    loadAnalytics()
  }, [startDate, endDate, period])

  const handleDateChange = (start: string, end: string) => {
    setStartDate(start)
    setEndDate(end)
  }

  const handlePeriodChange = (newPeriod: AggregationPeriod) => {
    setPeriod(newPeriod)
  }

  // Permission check wrapper - wraps entire component including loading/error states
  return (
    <PermissionGate
      permissions={['analytics.view']}
      fallback={
        <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
          <ShieldAlert className="h-12 w-12 text-gray-400 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Access Denied</h2>
          <p className="text-gray-500 dark:text-gray-400">You don't have permission to view analytics.</p>
        </div>
      }
    >
      <AnalyticsContent
        loading={loading}
        error={error}
        refreshing={refreshing}
        overview={overview}
        topTemplates={topTemplates}
        topUsers={topUsers}
        usageTrends={usageTrends}
        heatmapData={heatmapData}
        departmentUsage={departmentUsage}
        templateAnalytics={templateAnalytics}
        recentEvents={recentEvents}
        startDate={startDate}
        endDate={endDate}
        period={period}
        filters={filters}
        onDateChange={handleDateChange}
        onPeriodChange={handlePeriodChange}
        onRefresh={() => loadAnalytics(true)}
        onRetry={() => loadAnalytics()}
      />
    </PermissionGate>
  )
}

interface AnalyticsContentProps {
  loading: boolean
  error: string | null
  refreshing: boolean
  overview: TenantAnalyticsOverview | null
  topTemplates: TopTemplateAnalytics[]
  topUsers: TopUserAnalytics[]
  usageTrends: UsageTrendData[]
  heatmapData: HeatmapData[]
  departmentUsage: DepartmentUsage[]
  templateAnalytics: TemplateAnalytics[]
  recentEvents: TemplateUsageEvent[]
  startDate: string
  endDate: string
  period: AggregationPeriod
  filters: AnalyticsFilters
  onDateChange: (start: string, end: string) => void
  onPeriodChange: (period: AggregationPeriod) => void
  onRefresh: () => void
  onRetry: () => void
}

function AnalyticsContent({
  loading,
  error,
  refreshing,
  overview,
  topTemplates,
  topUsers,
  usageTrends,
  heatmapData,
  departmentUsage,
  templateAnalytics,
  recentEvents,
  startDate,
  endDate,
  period,
  filters,
  onDateChange,
  onPeriodChange,
  onRefresh,
  onRetry,
}: AnalyticsContentProps) {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="p-4 rounded-2xl bg-gray-100 dark:bg-gray-800">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Template Analytics"
          description="Track template usage and gain insights into user behavior"
        />
        <FluentCard className="flex flex-col items-center justify-center py-12">
          <div className="p-4 rounded-2xl bg-red-100 dark:bg-red-900/30 mb-4">
            <BarChart3 className="h-8 w-8 text-red-500 dark:text-red-400" />
          </div>
          <p className="text-red-600 dark:text-red-400 mb-4 font-medium">{error}</p>
          <Button onClick={onRetry}>Retry</Button>
        </FluentCard>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Template Analytics"
        description="Track template usage and gain insights into user behavior"
        actions={
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={onRefresh}
              disabled={refreshing}
              className="gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <ExportButton filters={filters} />
          </div>
        }
      />

      {/* Date Range Picker */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <AnalyticsDateRangePicker
          startDate={startDate}
          endDate={endDate}
          period={period}
          onDateChange={onDateChange}
          onPeriodChange={onPeriodChange}
        />
      </div>

      {/* Overview Cards */}
      <OverviewCards data={overview} loading={refreshing} />

      <Tabs defaultValue="overview" className="space-y-6">
        <div className="bg-white dark:bg-gray-900/50 rounded-2xl p-1.5 inline-flex border border-gray-100 dark:border-gray-800 shadow-sm">
          <TabsList className="bg-transparent border-0 gap-1">
            <TabsTrigger
              value="overview"
              className="data-[state=active]:bg-gray-900 data-[state=active]:text-white dark:data-[state=active]:bg-white dark:data-[state=active]:text-gray-900 rounded-xl px-4 py-2"
            >
              Overview
            </TabsTrigger>
            <TabsTrigger
              value="templates"
              className="data-[state=active]:bg-gray-900 data-[state=active]:text-white dark:data-[state=active]:bg-white dark:data-[state=active]:text-gray-900 rounded-xl px-4 py-2"
            >
              Templates
            </TabsTrigger>
            <TabsTrigger
              value="users"
              className="data-[state=active]:bg-gray-900 data-[state=active]:text-white dark:data-[state=active]:bg-white dark:data-[state=active]:text-gray-900 rounded-xl px-4 py-2"
            >
              Users
            </TabsTrigger>
            <TabsTrigger
              value="activity"
              className="data-[state=active]:bg-gray-900 data-[state=active]:text-white dark:data-[state=active]:bg-white dark:data-[state=active]:text-gray-900 rounded-xl px-4 py-2"
            >
              Activity
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="overview" className="space-y-6">
          {/* Usage Trends Chart */}
          <UsageTrendChart data={usageTrends} loading={refreshing} />

          {/* Two Column Layout: Top Templates / Top Users */}
          <div className="grid gap-6 lg:grid-cols-2">
            <TopTemplatesTable data={topTemplates} loading={refreshing} />
            <TopUsersTable data={topUsers} loading={refreshing} />
          </div>

          {/* Heatmap */}
          <UsageHeatmap data={heatmapData} loading={refreshing} />

          {/* Department Breakdown */}
          <DepartmentBreakdown data={departmentUsage} loading={refreshing} />
        </TabsContent>

        <TabsContent value="templates" className="space-y-6">
          {templateAnalytics.length > 0 ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {templateAnalytics.map((template) => (
                <TemplateAnalyticsCard
                  key={template.templateId}
                  data={template}
                  loading={refreshing}
                />
              ))}
            </div>
          ) : (
            <FluentCard className="text-center py-12">
              <BarChart3 className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">
                No template analytics data available for the selected period.
              </p>
            </FluentCard>
          )}
        </TabsContent>

        <TabsContent value="users" className="space-y-6">
          <div className="grid gap-6 lg:grid-cols-2">
            <TopUsersTable
              data={topUsers}
              loading={refreshing}
              title="Most Active Users"
              description="Users with the highest template activity"
            />
            <DepartmentBreakdown data={departmentUsage} loading={refreshing} />
          </div>
        </TabsContent>

        <TabsContent value="activity" className="space-y-6">
          <EventsTable data={recentEvents} loading={refreshing} />
          <UsageHeatmap data={heatmapData} loading={refreshing} />
        </TabsContent>
      </Tabs>
    </div>
  )
}
